// src/App.js
import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Header from './components/common/Header';
import AdminRoute from './components/common/AdminRoute';

// Eager-load auth components (needed immediately)
import Login from './components/auth/Login';
import PasswordReset from './components/auth/PasswordReset';

// Lazy-load all other components for code splitting
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const FlexibleQuotationForm = lazy(() => import('./components/quotation/FlexibleQuotationForm'));
const EditFlexibleQuotationForm = lazy(() => import('./components/quotation/EditFlexibleQuotationForm'));
const QuotationView = lazy(() => import('./components/quotation/QuotationView'));
const SharedQuotation = lazy(() => import('./components/quotation/SharedQuotation'));
const CatalogDashboard = lazy(() => import('./components/catalog/CatalogDashboard'));
const CatalogLayout = lazy(() => import('./components/catalog/CatalogLayout'));
const MaterialManagement = lazy(() => import('./components/catalog/MaterialManagement'));
const BrandManagement = lazy(() => import('./components/catalog/BrandManagement'));
const FinishManagement = lazy(() => import('./components/catalog/FinishManagement'));
const FurnitureCatalog = lazy(() => import('./components/catalog/FurnitureCatalog'));
const RoomTemplates = lazy(() => import('./components/catalog/RoomTemplates'));
const RoomTypeManagement = lazy(() => import('./components/catalog/RoomTypeManagement'));
const AnnexureTemplates = lazy(() => import('./components/catalog/AnnexureTemplates'));
const CustomerManagement = lazy(() => import('./components/customers/CustomerManagement'));
const Settings = lazy(() => import('./components/settings/Settings'));
const UserManagement = lazy(() => import('./components/users/UserManagement'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-100">
    <div className="text-center">
      <svg className="animate-spin h-12 w-12 mx-auto mb-4 text-[#E30613]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p className="text-lg font-medium text-gray-700">Loading...</p>
    </div>
  </div>
);

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="text-lg font-medium">Loading...</div>
    </div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Layout component with header
const AppLayout = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className={isAuthenticated ? 'pt-16' : ''}>
        {children}
      </main>
    </div>
  );
};

// Catalog route component with sidebar layout
const CatalogRoute = ({ element }) => {
  return (
    <ProtectedRoute>
      <CatalogLayout>
        {element}
      </CatalogLayout>
    </ProtectedRoute>
  );
};

// Quotation Edit Page - simplified to always use EditFlexibleQuotationForm
// EditFlexibleQuotationForm handles loading and error states internally
const QuotationEditPage = () => {
  return <EditFlexibleQuotationForm />;
};

function AppRoutes() {
  const { isLoading } = useAuth();
  const { companyInfo } = useTheme();
  
  // Apply favicon when app loads
  useEffect(() => {
    if (companyInfo?.favicon) {
      let faviconLink = document.querySelector('link[rel="icon"]');
      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        document.head.appendChild(faviconLink);
      }
      faviconLink.href = companyInfo.favicon;
    }
  }, [companyInfo]);
  
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="text-lg font-medium">Loading...</div>
    </div>;
  }
  
  return (
    <AppLayout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          {/* <Route path="/register" element={<Register />} /> */}
          
          {/* Public route for shared quotations */}
          <Route path="/shared/quotation/:id/:token" element={<SharedQuotation />} />
        
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/quotation/new" 
          element={
            <ProtectedRoute>
              <FlexibleQuotationForm />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/quotation/view/:id" 
          element={
            <ProtectedRoute>
              <QuotationView />
            </ProtectedRoute>
          } 
        />
        {/* New route for editing quotations */}
        <Route 
          path="/quotation/edit/:id" 
          element={
            <ProtectedRoute>
              <QuotationEditPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/catalog" 
          element={<CatalogRoute element={<CatalogDashboard />} />} 
        />
        <Route 
          path="/catalog/materials" 
          element={<CatalogRoute element={<MaterialManagement />} />} 
        />
        <Route 
          path="/catalog/brands" 
          element={<CatalogRoute element={<BrandManagement />} />} 
        />
        <Route 
          path="/catalog/finishes" 
          element={<CatalogRoute element={<FinishManagement />} />} 
        />
        <Route 
          path="/catalog/furniture" 
          element={<CatalogRoute element={<FurnitureCatalog />} />} 
        />
        <Route 
          path="/catalog/room-templates" 
          element={<CatalogRoute element={<RoomTemplates />} />} 
        />
        <Route 
          path="/catalog/room-types" 
          element={<CatalogRoute element={<RoomTypeManagement />} />} 
        />
        <Route 
          path="/catalog/annexure-templates" 
          element={<CatalogRoute element={<AnnexureTemplates />} />} 
        />
        <Route 
          path="/customer-management" 
          element={<ProtectedRoute><CustomerManagement /></ProtectedRoute>} 
        />
        <Route 
          path="/settings" 
          element={<ProtectedRoute><Settings /></ProtectedRoute>} 
        />
        <Route 
          path="/user-management" 
          element={<AdminRoute><UserManagement /></AdminRoute>} 
        />
        <Route 
          path="*" 
          element={<Navigate to="/dashboard" replace />} 
        />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppRoutes />
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;